// vg-binding: CONTEXT:decisions            (P15.D-20 rewire-no-new-ui, P15.D-07 audience-scoped fail-closed, P15.D-21 signal-only WS + optimistic mark-read)
// vg-binding: INTERFACE-STANDARDS:error-shape
// apps/vendor/src/features/notifications/canonical.ts
//
// Phase 15 (Notification & Email Consolidation) — Task 24
// Vendor bell re-wire onto the CANONICAL notification REST surface. Task 20
// merged the old `VendorNotification` collection into `notifications`
// (audience_type:'vendor'); Task 21 exposed the audience-scoped read/receipt
// endpoints. The audience is derived SERVER-SIDE from the portal JWT
// (`activeRoleSlot`) — the client CANNOT override it (P15.D-07). So the vendor
// portal automatically sees ONLY vendor-audience rows (no merchant leak).
//
//   GET   /api/v1/notifications              → { ok, data: CanonicalNotification[], meta:{nextCursor,hasMore,page?} }
//   GET   /api/v1/notifications/unread-count → { ok, data:{ count } }
//   PATCH /api/v1/notifications/:id/read     → { ok, data:{ id, read, readAt } }
//   POST  /api/v1/notifications/read-all     → { ok, data:{ marked, cutoff } }
//
// RENDER-AT-READ (P15.D-14): `title`/`body` are ALREADY rendered server-side at
// the caller's locale. The FE displays them verbatim — it does NOT re-render
// from `i18n_key` (that field is surfaced for forward-compat only).
//
// 2-CASE REVOKE (P15.D-07 / P15.D-21): a caller with ZERO resolvable audience
// gets `403 AUDIENCE_FORBIDDEN` on every read/mutation — the bell must render a
// BANNER + retry, NOT an empty state. A caller WITH ≥1 audience and zero rows
// gets `200` empty → real empty state. `NotificationApiError` carries the code
// so the component can tell the two apart.

import {
  useCallback,
} from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryKey,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { csrfFetch } from '../../lib/api/csrfFetch';

// ---------------------------------------------------------------------------
// Wire types (frozen canonical shape — P15.D-19)
// ---------------------------------------------------------------------------

export interface CanonicalNotification {
  id: string;
  /** Derived server-side as `event_type ?? category` — used for the tab filter. */
  type: string;
  /** Forward-compat only; the FE does NOT re-render from this (D-14). */
  i18n_key: string | null;
  i18n_params: Record<string, string | number> | null;
  /** Already rendered at the caller locale (D-14) — displayed verbatim. */
  title: string;
  body: string;
  /** Relative in-app path (nullable). */
  link: string | null;
  read: boolean;
  createdAt: string; // ISO-8601
}

export interface NotificationFeed {
  items: CanonicalNotification[];
  nextCursor: string | null;
  hasMore: boolean;
  page: number | null;
}

/**
 * Typed transport error carrying the canonical `error.code` so the bell can
 * distinguish `AUDIENCE_FORBIDDEN` (banner + retry) from a generic failure
 * (inline error) and from an empty-but-authorized feed (empty state).
 */
export class NotificationApiError extends Error {
  readonly code: string;
  readonly status: number;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'NotificationApiError';
    this.status = status;
    this.code = code;
  }
}

/** True iff the error is the 2-case-revoke `403 AUDIENCE_FORBIDDEN` signal. */
export function isAudienceForbidden(err: unknown): boolean {
  return err instanceof NotificationApiError && err.code === 'AUDIENCE_FORBIDDEN';
}

// ---------------------------------------------------------------------------
// Envelope parsing — canonical `{ok,data,meta}` / `{ok:false,error}` shape.
// A non-ok body (or parse failure) throws `NotificationApiError` so react-query
// routes it to `isError` / mutation `onError` (never a swallowed catch).
// ---------------------------------------------------------------------------

interface OkEnvelope<T> {
  ok: true;
  data: T;
  meta?: Record<string, unknown>;
}
interface ErrEnvelope {
  ok: false;
  error?: { code?: string; message?: string };
}

async function parseEnvelope<T>(
  res: Response,
): Promise<{ data: T; meta: Record<string, unknown> | undefined }> {
  let body: unknown;
  try {
    body = (await res.json()) as unknown;
  } catch {
    // Non-JSON body (e.g. 204/empty, or an HTML gateway/proxy page): fall
    // through to the `!ok` branch below, which THROWS a typed
    // NotificationApiError carrying the HTTP status. The parse error is
    // transformed into a surfaced error, never swallowed.
    body = null;
  }

  const ok =
    res.ok &&
    body !== null &&
    typeof body === 'object' &&
    (body as { ok?: unknown }).ok === true;

  if (!ok) {
    const err = (body as ErrEnvelope | null)?.error;
    throw new NotificationApiError(
      res.status,
      err?.code ?? `HTTP_${res.status}`,
      err?.message ?? 'Notification request failed',
    );
  }

  const okBody = body as OkEnvelope<T>;
  return { data: okBody.data, meta: okBody.meta };
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const NOTIF_FEED_KEY = ['vendor', 'notifications', 'canonical', 'feed'] as const;
export const NOTIF_UNREAD_KEY = [
  'vendor',
  'notifications',
  'canonical',
  'unread-count',
] as const;

/** Fail-closed retry: never re-hit on 401/403 (auth/audience is terminal). */
function retryUnlessAuth(failureCount: number, error: Error): boolean {
  if (
    error instanceof NotificationApiError &&
    (error.status === 401 || error.status === 403)
  ) {
    return false;
  }
  return failureCount < 2;
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export interface FeedParams {
  limit?: number;
  unreadOnly?: boolean;
  /** Gate the feed fetch behind panel-open so a closed bell never polls. */
  enabled?: boolean;
}

// ---------------------------------------------------------------------------
// Category derivation — the canonical `type` is `event_type ?? category` (a
// dotted event name or a bare category). Both the bell dropdown and the full
// page bucket it into the SAME tab set client-side (the canonical read layer is
// audience-scoped but not category-filtered — P15.D-20 rewire-only; no server
// category param, so filtering is uniform on the client for every surface).
// ---------------------------------------------------------------------------

export type NotificationTab = 'all' | 'orders' | 'products' | 'payments' | 'system';

export function deriveNotificationCategory(
  type: string,
): Exclude<NotificationTab, 'all'> {
  const t = type.toLowerCase();
  if (t === 'orders' || t === 'products' || t === 'payments' || t === 'system') {
    return t as Exclude<NotificationTab, 'all'>;
  }
  if (t.includes('order')) return 'orders';
  if (t.includes('product') || t.includes('stock') || t.includes('inventory')) {
    return 'products';
  }
  if (
    t.includes('pay') ||
    t.includes('earning') ||
    t.includes('payout') ||
    t.includes('wallet') ||
    t.includes('invoice')
  ) {
    return 'payments';
  }
  return 'system';
}

export function useNotificationFeed(
  params?: FeedParams,
): UseQueryResult<NotificationFeed, Error> {
  const limit = params?.limit;
  const unreadOnly = params?.unreadOnly;
  return useQuery({
    queryKey: [...NOTIF_FEED_KEY, { limit, unreadOnly }],
    enabled: params?.enabled ?? true,
    queryFn: async (): Promise<NotificationFeed> => {
      const qs = new URLSearchParams();
      if (limit) qs.set('limit', String(limit));
      if (unreadOnly) qs.set('unreadOnly', 'true');
      const qstr = qs.toString();
      const res = await csrfFetch(
        `/api/v1/notifications${qstr ? `?${qstr}` : ''}`,
        { credentials: 'include' },
      );
      const { data, meta } = await parseEnvelope<CanonicalNotification[]>(res);
      return {
        items: Array.isArray(data) ? data : [],
        nextCursor:
          typeof meta?.nextCursor === 'string' ? meta.nextCursor : null,
        hasMore: meta?.hasMore === true,
        page: typeof meta?.page === 'number' ? meta.page : null,
      };
    },
    refetchInterval: 30 * 1000,
    retry: retryUnlessAuth,
  });
}

export function useUnreadCount(): UseQueryResult<number, Error> {
  return useQuery({
    queryKey: NOTIF_UNREAD_KEY,
    queryFn: async (): Promise<number> => {
      const res = await csrfFetch('/api/v1/notifications/unread-count', {
        credentials: 'include',
      });
      const { data } = await parseEnvelope<{ count: number }>(res);
      return typeof data?.count === 'number' ? data.count : 0;
    },
    refetchInterval: 30 * 1000,
    retry: retryUnlessAuth,
  });
}

// ---------------------------------------------------------------------------
// Full-page feed — cursor-accumulated (offset/page not exposed by the canonical
// read layer for a client-filtered surface, so the page paginates via
// `Load more`). Shares the NOTIF_FEED_KEY prefix so the SAME mark-read /
// read-all mutations (below) optimistically patch it and the onSettled
// invalidation refetches both the bell dropdown AND this page together.
// ---------------------------------------------------------------------------

export interface PageFeedParams {
  /** Rows per fetched page. */
  limit?: number;
}

export function useNotificationFeedPages(
  params?: PageFeedParams,
): UseInfiniteQueryResult<InfiniteData<NotificationFeed>, Error> {
  const limit = params?.limit ?? 20;
  return useInfiniteQuery({
    queryKey: [...NOTIF_FEED_KEY, 'pages', { limit }],
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }): Promise<NotificationFeed> => {
      const qs = new URLSearchParams();
      qs.set('limit', String(limit));
      if (typeof pageParam === 'string' && pageParam.length > 0) {
        qs.set('cursor', pageParam);
      }
      const res = await csrfFetch(`/api/v1/notifications?${qs.toString()}`, {
        credentials: 'include',
      });
      const { data, meta } = await parseEnvelope<CanonicalNotification[]>(res);
      return {
        items: Array.isArray(data) ? data : [],
        nextCursor:
          typeof meta?.nextCursor === 'string' ? meta.nextCursor : null,
        hasMore: meta?.hasMore === true,
        page: typeof meta?.page === 'number' ? meta.page : null,
      };
    },
    getNextPageParam: (last) => (last.hasMore ? last.nextCursor : undefined),
    refetchInterval: 30 * 1000,
    retry: retryUnlessAuth,
  });
}

// ---------------------------------------------------------------------------
// Mutations — optimistic + rollback (P15.D-21, require-mutation-on-error)
// ---------------------------------------------------------------------------

interface MutationCtx {
  // Raw cached values — either a flat NotificationFeed (bell dropdown) or an
  // InfiniteData<NotificationFeed> (full page). Typed `unknown` so the rollback
  // restores whichever shape was captured.
  prevFeeds: Array<[QueryKey, unknown]>;
  prevCount: number | undefined;
}

/** Patch a single flat feed page's read-state. */
function patchFeedRead(
  feed: NotificationFeed,
  predicate: (n: CanonicalNotification) => boolean,
): NotificationFeed {
  return {
    ...feed,
    items: feed.items.map((n) =>
      predicate(n) && !n.read ? { ...n, read: true } : n,
    ),
  };
}

/**
 * Optimistically flip a matching row read in every cached feed — supports BOTH
 * the flat `NotificationFeed` (bell dropdown) and the
 * `InfiniteData<NotificationFeed>` full-page shape. Any other cached value is
 * skipped untouched.
 */
function optimisticMarkRead(
  feeds: Array<[QueryKey, unknown]>,
  predicate: (n: CanonicalNotification) => boolean,
): Array<[QueryKey, unknown]> {
  const out: Array<[QueryKey, unknown]> = [];
  for (const [key, cached] of feeds) {
    if (!cached || typeof cached !== 'object') continue;
    if (Array.isArray((cached as { pages?: unknown }).pages)) {
      const inf = cached as InfiniteData<NotificationFeed>;
      out.push([
        key,
        {
          ...inf,
          pages: inf.pages.map((p) => patchFeedRead(p, predicate)),
        },
      ]);
    } else if (Array.isArray((cached as { items?: unknown }).items)) {
      out.push([key, patchFeedRead(cached as NotificationFeed, predicate)]);
    }
  }
  return out;
}

export function useMarkNotificationRead(): UseMutationResult<
  { id: string; read: true },
  Error,
  string,
  MutationCtx
> {
  const qc = useQueryClient();
  return useMutation<{ id: string; read: true }, Error, string, MutationCtx>({
    mutationFn: async (id) => {
      const res = await csrfFetch(
        `/api/v1/notifications/${encodeURIComponent(id)}/read`,
        { method: 'PATCH', credentials: 'include' },
      );
      const { data } = await parseEnvelope<{ id: string; read: true }>(res);
      return data;
    },
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: NOTIF_FEED_KEY });
      await qc.cancelQueries({ queryKey: NOTIF_UNREAD_KEY });
      const prevFeeds = qc.getQueriesData({
        queryKey: NOTIF_FEED_KEY,
      });
      const prevCount = qc.getQueryData<number>(NOTIF_UNREAD_KEY);
      for (const [key, feed] of optimisticMarkRead(
        prevFeeds,
        (n) => n.id === id,
      )) {
        qc.setQueryData(key, feed);
      }
      if (typeof prevCount === 'number') {
        qc.setQueryData(NOTIF_UNREAD_KEY, Math.max(0, prevCount - 1));
      }
      return { prevFeeds, prevCount };
    },
    onError: (_err, _id, ctx) => {
      // Rollback badge + list so the UI never shows a phantom read state.
      // The caller surfaces the toast via its per-call `onError` (D-21).
      if (!ctx) return;
      for (const [key, feed] of ctx.prevFeeds) qc.setQueryData(key, feed);
      if (ctx.prevCount !== undefined) {
        qc.setQueryData(NOTIF_UNREAD_KEY, ctx.prevCount);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: NOTIF_FEED_KEY });
      void qc.invalidateQueries({ queryKey: NOTIF_UNREAD_KEY });
    },
  });
}

export function useMarkAllNotificationsRead(): UseMutationResult<
  { marked: number },
  Error,
  void,
  MutationCtx
> {
  const qc = useQueryClient();
  return useMutation<{ marked: number }, Error, void, MutationCtx>({
    mutationFn: async () => {
      const res = await csrfFetch('/api/v1/notifications/read-all', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const { data } = await parseEnvelope<{ marked: number }>(res);
      return data;
    },
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: NOTIF_FEED_KEY });
      await qc.cancelQueries({ queryKey: NOTIF_UNREAD_KEY });
      const prevFeeds = qc.getQueriesData({
        queryKey: NOTIF_FEED_KEY,
      });
      const prevCount = qc.getQueryData<number>(NOTIF_UNREAD_KEY);
      for (const [key, feed] of optimisticMarkRead(prevFeeds, () => true)) {
        qc.setQueryData(key, feed);
      }
      qc.setQueryData(NOTIF_UNREAD_KEY, 0);
      return { prevFeeds, prevCount };
    },
    onError: (_err, _v, ctx) => {
      if (!ctx) return;
      for (const [key, feed] of ctx.prevFeeds) qc.setQueryData(key, feed);
      if (ctx.prevCount !== undefined) {
        qc.setQueryData(NOTIF_UNREAD_KEY, ctx.prevCount);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: NOTIF_FEED_KEY });
      void qc.invalidateQueries({ queryKey: NOTIF_UNREAD_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// WS signal → refetch (P15.D-21: WS is SIGNAL-ONLY; FE refetches, never
// self-increments/self-prepends). Returns a stable invalidator the bell binds
// to the vendor socket events.
// ---------------------------------------------------------------------------

export function useNotificationRefetch(): () => void {
  const qc = useQueryClient();
  return useCallback(() => {
    void qc.invalidateQueries({ queryKey: NOTIF_FEED_KEY });
    void qc.invalidateQueries({ queryKey: NOTIF_UNREAD_KEY });
  }, [qc]);
}
